/**
 * InsertColumnPill — one entry of an INSERT's explicit column list: the name
 * (a column picker when editing — picking swaps the column, the VALUES cells
 * stay put) plus the remove `×`. A name the target table doesn't have carries
 * a warning glyph. `InsertColumnMap` owns the shape / readiness gating and
 * passes the resolved remove state in.
 */

import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyInsertColumn } from '../../edit/edits/modifyColumnTargets';
import { removeInsertColumn } from '../../edit/edits/removeInsertColumn';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import type { WrittenTableColumns } from '../update/assignmentColumns';
import { DmlColumnPicker } from '../update/DmlColumnPicker';
import { UnknownColumnMark } from '../update/UnknownColumnMark';
import { recordEditFailure, safeExtractStructure } from './insertColumnMapHelpers';

export interface InsertColumnPillProps {
  readonly testIdPrefix: string;
  readonly index: number;
  readonly column: string;
  readonly columns: readonly string[];
  /** Live editing — the name becomes a picker. Absent on scaffold / MERGE mounts. */
  readonly edit?: { readonly editContext: VqeEditContext; readonly table: WrittenTableColumns };
  readonly canRemove: boolean;
  readonly removeTooltip?: string;
  /** Enabled-× consequence hint (DML-37); undefined when `canRemove` is false. */
  readonly removeHint?: string;
}

export function InsertColumnPill({
  testIdPrefix,
  index,
  column,
  columns,
  edit,
  canRemove,
  removeTooltip,
  removeHint,
}: InsertColumnPillProps) {
  return (
    <li
      className="flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5"
      data-testid={`${testIdPrefix}-column-${index}`}
    >
      {edit ? (
        <DmlColumnPicker
          testId={`${testIdPrefix}-column-text-${index}`}
          table={edit.table}
          takenRefs={columns}
          qualifier={null}
          usage="insert"
          triggerLabel={column}
          triggerVariant="inline"
          triggerClassName="font-mono text-xs"
          onPick={({ text }) => {
            void modifyInsertColumn(index, text, toCommitCtx(edit.editContext));
          }}
        />
      ) : (
        <span className="font-mono text-xs" data-testid={`${testIdPrefix}-column-text-${index}`}>
          {column}
        </span>
      )}
      {edit ? (
        <UnknownColumnMark columnRef={column} table={edit.table} testId={`${testIdPrefix}-column-unknown-${index}`} />
      ) : null}
      {/*
        RemoveButton's OWN tooltip only ever surfaces while `disabled` (see
        `RemoveButton.tsx`). The DML-37 consequence hint is an ENABLED-case
        tooltip, so it's an OUTER VqeTooltip here rather than a change to the
        shared button: when `canRemove` is true, RemoveButton's internal
        tooltip content is `undefined` (bypasses its own Tooltip wrapper), so
        this outer one wraps the bare button with no nesting conflict. When
        `canRemove` is false `removeHint` is undefined too, so the outer
        wrapper bypasses and the disabled tooltip behavior is untouched.
      */}
      <VqeTooltip selfProvider content={removeHint}>
        <RemoveButton
          testId={`${testIdPrefix}-column-remove-${index}`}
          ariaLabel="Remove column"
          disabled={!canRemove}
          tooltip={removeTooltip}
          onClick={
            canRemove && edit
              ? () => {
                  removeInsertColumn(index, toCommitCtx(edit.editContext), {
                    structure: safeExtractStructure(edit.editContext),
                  }).catch(recordEditFailure);
                }
              : undefined
          }
        />
      </VqeTooltip>
    </li>
  );
}
